import { expressionFunction } from "vega";
import type { VisualizationSpec } from "vega-embed";
import {
  encodeNumberFormat,
  formatEncoded,
  isDefaultNumberFormat,
  NUMBER_FORMAT_TYPE,
  toD3Format,
} from "./numberFormat";
import type { ModelNumberFormat, NumberFormatContext, NumberFormatSettings } from "./numberFormat";

type SpecObject = Record<string, unknown>;

const asObject = (value: unknown): SpecObject | undefined =>
  value !== null && typeof value === "object" && !Array.isArray(value) ? (value as SpecObject) : undefined;

let registered = false;
/** Makes `formatType: "magicbiNumber"` resolvable in Vega. Idempotent. */
export const registerNumberFormatType = (): void => {
  if (registered) return;
  expressionFunction(NUMBER_FORMAT_TYPE, formatEncoded);
  registered = true;
};

/** The `format` (and `formatType`) properties for one place a number shows. */
const formatProps = (
  settings: NumberFormatSettings | undefined,
  model: ModelNumberFormat | undefined,
  context: NumberFormatContext,
): { format: string; formatType?: string } => {
  const d3 = toD3Format(settings, model, context);
  if (d3 !== undefined) return { format: d3 };
  registerNumberFormatType();
  return { format: encodeNumberFormat(settings, model, context), formatType: NUMBER_FORMAT_TYPE };
};

const withoutFormatType = (definition: SpecObject): SpecObject => {
  const { formatType: _formatType, ...rest } = definition;
  return rest;
};

const isQuantitative = (definition: SpecObject): boolean => definition.type === "quantitative";

const formatAxis = (channel: SpecObject, props: { format: string; formatType?: string }): SpecObject => {
  if (channel.axis === null) return channel;
  return { ...channel, axis: { ...withoutFormatType(asObject(channel.axis) ?? {}), ...props } };
};

const formatLegend = (channel: SpecObject, props: { format: string; formatType?: string }): SpecObject =>
  channel.legend === null
    ? channel
    : { ...channel, legend: { ...withoutFormatType(asObject(channel.legend) ?? {}), ...props } };

const formatField = (definition: SpecObject, props: { format: string; formatType?: string }): SpecObject => ({
  ...withoutFormatType(definition),
  ...props,
});

const formatUnit = (unit: SpecObject, settings: NumberFormatSettings | undefined, model: ModelNumberFormat | undefined): SpecObject => {
  const encoding = asObject(unit.encoding);
  if (!encoding) return unit;
  const axisProps = formatProps(settings, model, "axis");
  const valueProps = formatProps(settings, model, "value");
  const next: SpecObject = { ...encoding };
  (["x", "y"] as const).forEach((key) => {
    const channel = asObject(encoding[key]);
    if (channel && isQuantitative(channel)) next[key] = formatAxis(channel, axisProps);
  });
  const color = asObject(encoding.color);
  if (color && isQuantitative(color)) next.color = formatLegend(color, valueProps);
  (["size", "opacity"] as const).forEach((key) => {
    const channel = asObject(encoding[key]);
    if (channel && isQuantitative(channel)) next[key] = formatLegend(channel, valueProps);
  });
  const text = asObject(encoding.text);
  if (text && isQuantitative(text)) next.text = formatField(text, valueProps);
  const tooltip = encoding.tooltip;
  if (Array.isArray(tooltip)) {
    next.tooltip = tooltip.map((entry) => {
      const field = asObject(entry);
      return field && isQuantitative(field) ? formatField(field, valueProps) : entry;
    });
  } else {
    const field = asObject(tooltip);
    if (field && isQuantitative(field)) next.tooltip = formatField(field, valueProps);
  }
  return { ...unit, encoding: next };
};

const COMPOSITES = ["layer", "concat", "hconcat", "vconcat"] as const;

const walk = (node: SpecObject, settings: NumberFormatSettings | undefined, model: ModelNumberFormat | undefined): SpecObject => {
  let next = node;
  COMPOSITES.forEach((key) => {
    if (Array.isArray(node[key])) {
      next = { ...next, [key]: (node[key] as unknown[]).map((child) => {
        const object = asObject(child);
        return object ? walk(object, settings, model) : child;
      }) };
    }
  });
  const inner = asObject(node.spec);
  if (inner) next = { ...next, spec: walk(inner, settings, model) };
  return node.encoding ? formatUnit(next, settings, model) : next;
};

/**
 * Writes the number format onto every quantitative axis, legend, text mark and tooltip field of a
 * single, layered, concatenated or faceted Vega-Lite spec. Pure: the input spec is not mutated.
 * Selection and palette code are not touched. With nothing set and no model unit it still applies
 * the defaults (thousands separators, up to 2 decimals, compact axes for large values).
 */
export const applyNumberFormat = (
  spec: VisualizationSpec,
  settings?: NumberFormatSettings,
  model?: ModelNumberFormat,
): VisualizationSpec => {
  const root = asObject(spec);
  if (!root) return spec;
  const effective = isDefaultNumberFormat(settings) ? undefined : settings;
  return walk(root, effective, model) as VisualizationSpec;
};
